/**
 * Format Engine
 * 
 * Main orchestration system that coordinates field detection, formatter selection,
 * and real-time formatting application. Provides the primary interface between
 * the field detection system and the formatting system.
 */

import { getFormatterRegistry, FormatterRegistry } from './formatters/formatter-registry';
import { BaseFormatter, ExtendedFormattingResult } from './formatters/base-formatter';
import { getFieldRegistry, FieldRegistry, FieldInfo } from './field-registry';
import { FieldType } from './detection-patterns';
import { FormattingOptions, ValidationResult, FormattingEvent, FormattingEventCallback } from './types/formatting-types';

/**
 * Format engine configuration
 */
export interface FormatEngineConfig {
  /** Enable debug logging */
  debug?: boolean;
  /** Enable real-time progressive formatting */
  enableProgressiveFormatting?: boolean;
  /** Enable validation during formatting */
  enableValidation?: boolean;
  /** Throttle formatting operations (milliseconds) */
  formatThrottle?: number;
  /** Enable automatic error recovery */
  enableErrorRecovery?: boolean;
  /** CSS classes for visual feedback */
  cssClasses?: {
    formatting?: string;
    valid?: string;
    invalid?: string;
    warning?: string;
  };
}

/**
 * Format operation result
 */
export interface FormatOperationResult {
  /** Whether the operation succeeded */
  success: boolean;
  /** The formatted value */
  formattedValue: string;
  /** The raw value */
  rawValue: string;
  /** Field that was formatted */
  fieldInfo: FieldInfo;
  /** Validation result if validation was performed */
  validation?: ValidationResult;
  /** Any errors that occurred */
  errors: string[];
  /** Operation timestamp */
  timestamp: Date;
  /** Whether the value was modified during formatting */
  wasModified?: boolean;
}

/**
 * Main format engine that coordinates all formatting operations
 */
export class FormatEngine {
  private config: Required<FormatEngineConfig>;
  private formatterRegistry: FormatterRegistry;
  private fieldRegistry: FieldRegistry;
  private eventListeners: FormattingEventCallback[] = [];
  private formatTimers = new WeakMap<HTMLElement, NodeJS.Timeout>();

  private static readonly DEFAULT_CONFIG: Required<FormatEngineConfig> = {
    debug: false,
    enableProgressiveFormatting: true,
    enableValidation: true,
    formatThrottle: 50, // 50ms throttle for performance
    enableErrorRecovery: true,
    cssClasses: {
      formatting: 'formatting-active',
      valid: 'field-valid',
      invalid: 'field-invalid',
      warning: 'field-warning'
    }
  };

  constructor(config: FormatEngineConfig = {}) {
    this.config = { ...FormatEngine.DEFAULT_CONFIG, ...config };
    this.formatterRegistry = getFormatterRegistry();
    this.fieldRegistry = getFieldRegistry(this.config.debug);
  }

  /**
   * Initialize the format engine
   */
  public initialize(): void {
    this.log('Format engine initializing...');
    
    // Set up event listeners for real-time formatting
    if (this.config.enableProgressiveFormatting) {
      this.setupProgressiveFormatting();
    }

    this.log('Format engine initialized');
  }

  /**
   * Format a specific field
   */
  public formatField(
    element: HTMLInputElement | HTMLTextAreaElement,
    options?: FormattingOptions,
    isProgressive: boolean = false
  ): FormatOperationResult {
    const startTime = Date.now();
    
    try {
      // Get field information
      const fieldInfo = this.fieldRegistry.getFieldInfo(element);
      if (!fieldInfo) {
        return {
          success: false,
          formattedValue: element.value,
          rawValue: element.value,
          fieldInfo: this.createBasicFieldInfo(element),
          errors: ['Field not registered or detected'],
          timestamp: new Date()
        };
      }

      // Get appropriate formatter
      const formatter = this.formatterRegistry.getFormatterByFieldType(fieldInfo.fieldType);
      if (!formatter) {
        return {
          success: false,
          formattedValue: element.value,
          rawValue: element.value,
          fieldInfo,
          errors: [`No formatter available for field type: ${fieldInfo.fieldType}`],
          timestamp: new Date()
        };
      }

      // Get current cursor position for progressive formatting
      let cursorPosition: number | undefined;
      if (isProgressive && document.activeElement === element) {
        cursorPosition = element.selectionStart || undefined;
      }

      // Perform formatting
      const formatResult = formatter.format(
        element.value,
        options,
        cursorPosition,
        isProgressive
      );

      // Update element value if formatting was successful and value changed
      if (formatResult.success && formatResult.wasModified) {
        try {
          // Simple and clean: just set the formatted value
          // No HTML5 validation conflicts since all fields are type="text"
          element.value = formatResult.formattedValue;
          
          // Restore cursor position if available and value was actually set
          if (isProgressive && formatResult.newCursorPosition !== undefined && document.activeElement === element) {
            try {
              element.setSelectionRange(formatResult.newCursorPosition, formatResult.newCursorPosition);
            } catch (error) {
              this.log(`Cursor positioning error: ${error}`, 'warn');
            }
          }
        } catch (error) {
          // This should rarely happen with type="text" fields, but keep for safety
          this.log(`Failed to set formatted value: ${error}`, 'warn');
          return {
            success: false,
            formattedValue: element.value,
            rawValue: formatter.extractRaw ? formatter.extractRaw(element.value) : element.value,
            fieldInfo,
            errors: [error instanceof Error ? error.message : String(error)],
            timestamp: new Date(),
            wasModified: false
          };
        }
      }

      // Perform validation if enabled
      let validationResult: ValidationResult | undefined;
      if (this.config.enableValidation && !isProgressive) {
        validationResult = formatter.validate(formatResult.formattedValue, options);
        this.applyValidationFeedback(element, validationResult);
      }

      // Update field info
      this.updateFieldInfo(fieldInfo, formatResult, validationResult);

      // Emit formatting event
      this.emitFormattingEvent('format', fieldInfo, {
        formatResult,
        validationResult,
        isProgressive,
        duration: Date.now() - startTime
      });

      const result: FormatOperationResult = {
        success: formatResult.success,
        formattedValue: formatResult.formattedValue,
        rawValue: formatResult.rawValue,
        fieldInfo,
        validation: validationResult,
        errors: formatResult.errors || [],
        timestamp: new Date()
      };

      return result;

    } catch (error) {
      const errorMsg = `Format operation failed: ${error}`;
      this.log(errorMsg, 'error');

      if (this.config.enableErrorRecovery) {
        // Attempt graceful recovery
        return this.recoverFromFormatError(element, error as Error);
      } else {
        throw error;
      }
    }
  }

  /**
   * Format all registered fields
   */
  public formatAllFields(options?: FormattingOptions): FormatOperationResult[] {
    const results: FormatOperationResult[] = [];
    const allFields = this.fieldRegistry.getAllFields();

    for (const fieldInfo of allFields) {
      try {
        const result = this.formatField(fieldInfo.element as HTMLInputElement, options, false);
        results.push(result);
      } catch (error) {
        this.log(`Error formatting field ${fieldInfo.dataName}: ${error}`, 'error');
        results.push({
          success: false,
          formattedValue: fieldInfo.currentValue,
          rawValue: fieldInfo.currentValue,
          fieldInfo,
          errors: [`Formatting failed: ${error}`],
          timestamp: new Date()
        });
      }
    }

    return results;
  }

  /**
   * Validate a specific field
   */
  public validateField(
    element: HTMLInputElement | HTMLTextAreaElement,
    options?: FormattingOptions
  ): ValidationResult {
    try {
      const fieldInfo = this.fieldRegistry.getFieldInfo(element);
      if (!fieldInfo) {
        return {
          isValid: false,
          errors: ['Field not registered or detected'],
          warnings: []
        };
      }

      const formatter = this.formatterRegistry.getFormatterByFieldType(fieldInfo.fieldType);
      if (!formatter) {
        return {
          isValid: false,
          errors: [`No formatter available for field type: ${fieldInfo.fieldType}`],
          warnings: []
        };
      }

      const validationResult = formatter.validate(element.value, options);
      this.applyValidationFeedback(element, validationResult);

      return validationResult;

    } catch (error) {
      this.log(`Validation error: ${error}`, 'error');
      return {
        isValid: false,
        errors: [`Validation failed: ${error}`],
        warnings: []
      };
    }
  }

  /**
   * Add event listener for formatting events
   */
  public addEventListener(callback: FormattingEventCallback): void {
    this.eventListeners.push(callback);
  }

  /**
   * Remove event listener
   */
  public removeEventListener(callback: FormattingEventCallback): void {
    const index = this.eventListeners.indexOf(callback);
    if (index > -1) {
      this.eventListeners.splice(index, 1);
    }
  }

  /**
   * Get engine statistics
   */
  public getStats(): {
    fieldsRegistered: number;
    formattersAvailable: number;
    supportedFieldTypes: FieldType[];
    lastFormatTime?: Date;
  } {
    return {
      fieldsRegistered: this.fieldRegistry.getRegisteredFieldCount(),
      formattersAvailable: this.formatterRegistry.getAllFormatters().length,
      supportedFieldTypes: this.formatterRegistry.getSupportedFieldTypes(),
      lastFormatTime: this.fieldRegistry.getLastScanTime() || undefined
    };
  }

  /**
   * Cleanup engine resources
   */
  public cleanup(): void {
    this.log('Format engine cleaning up...');
    
    // Clear all timers
    this.formatTimers = new WeakMap();
    
    // Clear event listeners
    this.eventListeners = [];
    
    this.log('Format engine cleanup complete');
  }

  /**
   * Setup progressive formatting event listeners
   */
  private setupProgressiveFormatting(): void {
    // Use event delegation for better performance
    document.addEventListener('input', (event) => {
      const target = event.target as HTMLElement;
      if (this.isFormattableField(target)) {
        this.handleProgressiveFormat(target as HTMLInputElement);
      }
    });

    document.addEventListener('focus', (event) => {
      const target = event.target as HTMLElement;
      if (this.isFormattableField(target)) {
        this.handleFieldFocus(target as HTMLInputElement);
      }
    });

    document.addEventListener('blur', (event) => {
      const target = event.target as HTMLElement;
      if (this.isFormattableField(target)) {
        this.handleFieldBlur(target as HTMLInputElement);
      }
    });
  }

  /**
   * Handle progressive formatting with throttling
   */
  private handleProgressiveFormat(element: HTMLInputElement): void {
    // Clear existing timer
    const existingTimer = this.formatTimers.get(element);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set new timer
    const timer = setTimeout(() => {
      this.formatField(element, {}, true);
    }, this.config.formatThrottle);

    this.formatTimers.set(element, timer);
  }

  /**
   * Handle field focus events
   */
  private handleFieldFocus(element: HTMLInputElement): void {
    if (this.config.cssClasses?.formatting) {
      element.classList.add(this.config.cssClasses.formatting);
    }
    
    // For SSN fields, remove masking during editing
    const fieldInfo = this.fieldRegistry.getFieldInfo(element);
    if (fieldInfo?.fieldType === FieldType.SSN) {
      // This would be handled by the SSN formatter
      this.log('SSN field focused - masking should be removed');
    }
  }

  /**
   * Handle field blur events
   */
  private handleFieldBlur(element: HTMLInputElement): void {
    if (this.config.cssClasses?.formatting) {
      element.classList.remove(this.config.cssClasses.formatting);
    }
    
    // Perform final formatting and validation
    const result = this.formatField(element, {}, false);
    
    // For SSN fields, apply masking after blur
    if (result.fieldInfo.fieldType === FieldType.SSN) {
      // This would be handled by the SSN formatter
      this.log('SSN field blurred - masking should be applied');
    }
  }

  /**
   * Check if an element is a formattable field
   */
  private isFormattableField(element: HTMLElement): boolean {
    return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;
  }

  /**
   * Apply validation feedback to element
   */
  private applyValidationFeedback(element: HTMLElement, validation: ValidationResult): void {
    // Remove existing validation classes
    if (this.config.cssClasses?.valid) {
      element.classList.remove(this.config.cssClasses.valid);
    }
    if (this.config.cssClasses?.invalid) {
      element.classList.remove(this.config.cssClasses.invalid);
    }
    if (this.config.cssClasses?.warning) {
      element.classList.remove(this.config.cssClasses.warning);
    }

    // Apply appropriate class
    if (validation.isValid && this.config.cssClasses?.valid) {
      element.classList.add(this.config.cssClasses.valid);
    } else if (!validation.isValid && this.config.cssClasses?.invalid) {
      element.classList.add(this.config.cssClasses.invalid);
    }

    if (validation.warnings && validation.warnings.length > 0 && this.config.cssClasses?.warning) {
      element.classList.add(this.config.cssClasses.warning);
    }
  }

  /**
   * Update field info with formatting results
   */
  private updateFieldInfo(
    fieldInfo: FieldInfo,
    formatResult: ExtendedFormattingResult,
    validationResult?: ValidationResult
  ): void {
    fieldInfo.currentValue = formatResult.formattedValue;
    fieldInfo.formatApplied = formatResult.success;
    
    if (validationResult) {
      fieldInfo.isValid = validationResult.isValid;
      fieldInfo.lastError = validationResult.errors.join(', ') || undefined;
    }
  }

  /**
   * Create basic field info for unregistered fields
   */
  private createBasicFieldInfo(element: HTMLElement): FieldInfo {
    return {
      element: element as any,
      dataName: element.getAttribute('data-name'),
      fieldType: FieldType.UNKNOWN,
      detection: {
        fieldType: FieldType.UNKNOWN,
        confidence: 0,
        method: 'none',
        requiresFormatting: false
      },
      currentValue: (element as HTMLInputElement).value || '',
      isActive: document.activeElement === element,
      detectedAt: new Date()
    };
  }

  /**
   * Recover from formatting errors
   */
  private recoverFromFormatError(element: HTMLElement, error: Error): FormatOperationResult {
    this.log(`Recovering from format error: ${error.message}`, 'warn');
    
    return {
      success: false,
      formattedValue: (element as HTMLInputElement).value || '',
      rawValue: (element as HTMLInputElement).value || '',
      fieldInfo: this.createBasicFieldInfo(element),
      errors: [`Format recovery: ${error.message}`],
      timestamp: new Date()
    };
  }

  /**
   * Emit formatting event to listeners
   */
  private emitFormattingEvent(
    type: FormattingEvent['type'],
    fieldInfo: FieldInfo,
    data?: any
  ): void {
    const event: FormattingEvent = {
      type,
      target: {
        element: fieldInfo.element as HTMLInputElement,
        config: {
          type: fieldInfo.fieldType as any,
          realTime: this.config.enableProgressiveFormatting,
          validate: this.config.enableValidation
        },
        formattedValue: fieldInfo.currentValue,
        rawValue: fieldInfo.currentValue,
        isValid: fieldInfo.isValid,
        lastValidation: fieldInfo.lastError ? {
          isValid: false,
          errors: [fieldInfo.lastError],
          warnings: []
        } : undefined
      },
      data,
      timestamp: new Date()
    };

    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        this.log(`Event listener error: ${error}`, 'error');
      }
    });
  }

  /**
   * Log messages with debug mode check
   */
  private log(message: string, level: 'log' | 'warn' | 'error' = 'log'): void {
    if (this.config.debug) {
      console[level](`[FormatEngine] ${message}`);
    }
  }

  // 🎉 HTML5 validation compatibility methods removed!
  // All fields are now type="text" so no validation conflicts!
}

/**
 * Global format engine instance
 */
let globalFormatEngine: FormatEngine | null = null;

/**
 * Get or create the global format engine instance
 */
export function getFormatEngine(config?: FormatEngineConfig): FormatEngine {
  if (!globalFormatEngine) {
    globalFormatEngine = new FormatEngine(config);
  }
  return globalFormatEngine;
}

/**
 * Reset the global format engine instance (primarily for testing)
 */
export function resetFormatEngine(): void {
  if (globalFormatEngine) {
    globalFormatEngine.cleanup();
  }
  globalFormatEngine = null;
}

/**
 * Quick format function for convenience
 */
export function quickFormat(element: HTMLInputElement | HTMLTextAreaElement): FormatOperationResult {
  return getFormatEngine().formatField(element);
}